/**
 * @file RgsMode.ts
 * @role Resolves WHICH backend this build talks to, from one value instead of a cross product
 *       of booleans. The only place allowed to combine the environment override with the
 *       config default, and the only place that enforces the production mock refusal.
 * @owns The RgsMode vocabulary and its resolution rules.
 * @reads GameEnvironment's raw VITE_RGS_MODE value, validated game config (mockRgs.enabled).
 * @neverDoes Construct clients. Read import.meta.env (that boundary is GameEnvironment's).
 *            Decide outcomes. Touch Pixi.
 * @invariants 6, 13.
 * @see docs/BACKEND_CONTRACT.md, docs/RUN_MODES.md
 */
import { GameError } from '@core/errors/GameError';

/**
 * The three backends this game can be pointed at. There is only ever ONE client
 * (`HttpRgsClient`) and one source of mock outcomes (`src/mocks/rgsScenarios.ts`); the modes
 * differ only in who answers the HTTP call and how fast:
 * - `mock` — the MSW fake server with ZERO latency. The default for dev and mock-preview.
 * - `msw`  — the same MSW fake server with the configured `responseDelayMs`, so the states
 *            that exist only while a request is in flight (locked HUD, watchdog, minimum spin
 *            duration) are exercised. Dev/mock-preview only, like `mock`.
 * - `live` — the deployed RGS at `rgs.baseUrl`. The only mode production allows.
 */
export type RgsMode = 'mock' | 'msw' | 'live';

const RGS_MODES: readonly RgsMode[] = ['mock', 'msw', 'live'];

/** Modes that stand in for a real server, and so may never run in a production artifact. */
const FAKE_MODES: readonly RgsMode[] = ['mock', 'msw'];

export interface RgsModeEnvironment {
  readonly isDev: boolean;
  /** True only for the bundled, test-only `vite --mode mock-preview` artifact. */
  readonly isMockPreview: boolean;
  /** Raw `VITE_RGS_MODE`; empty means "use the config default". */
  readonly rgsMode: string;
}

export interface RgsModeConfig {
  readonly mockRgs: { readonly enabled: boolean };
}

/**
 * Read an explicit mode value. Returns undefined when unset OR unrecognised — callers that
 * only need to know "is this msw?" must not throw on a typo before the app has a canvas to
 * report it on. `resolveRgsMode` is the one place that turns an unrecognised value into a
 * loud failure.
 */
export function parseRgsMode(raw: string): RgsMode | undefined {
  const value = raw.trim();
  return RGS_MODES.find((mode) => mode === value);
}

/**
 * Decide the backend for this build. Rules, in order:
 * 1. An explicit `VITE_RGS_MODE` wins.
 * 2. Otherwise fall back to the config default (`mockRgs.enabled ? 'mock' : 'live'`).
 * 3. A fake backend outside dev/mock-preview is fatal (Invariant 6).
 * 4. An unrecognised value is fatal rather than silently falling back.
 *
 * @throws GameError CONFIG_LOAD_FAILED — a configuration mistake no user retry can fix.
 */
export function resolveRgsMode(environment: RgsModeEnvironment, config: RgsModeConfig): RgsMode {
  const explicit = environment.rgsMode.trim();
  let mode: RgsMode;

  if (explicit) {
    const parsed = parseRgsMode(explicit);
    if (!parsed) {
      throw new GameError(
        'CONFIG_LOAD_FAILED',
        `Unknown VITE_RGS_MODE "${explicit}"; expected one of ${RGS_MODES.join(', ')}.`,
      );
    }
    mode = parsed;
  } else {
    mode = config.mockRgs.enabled ? 'mock' : 'live';
  }

  if (FAKE_MODES.includes(mode) && !environment.isDev && !environment.isMockPreview) {
    // Wording is load-bearing: the production smoke test asserts this exact guard fires in the
    // real artifact (tests/e2e-preview/prod-smoke.spec.ts).
    throw new GameError(
      'CONFIG_LOAD_FAILED',
      `Mock RGS is enabled outside dev/test (mode "${mode}"); configure a real RGS client ` +
        'before production.',
    );
  }

  return mode;
}
